import { useRef, useState } from 'react';
import { useDateNow, useDispatch, useReduxState } from 'hooks';
import { addDays, addMonths } from 'date-fns';
import { toDate } from 'date-fns-tz';
import api from 'api';
import { breakpoints } from 'styles';
import { FlexContainer, ModalButtonWithForm } from 'components/common';
import { Datepicker, FormGroup, UserSelector } from 'components/common/form';
import { UserPasswordReview } from 'components/users';
import {
  AchievementCompletionsAddHeader,
  AchievementCompletionsAddInfo,
  AchievementCompletionsAddTable,
  AchievementImportJson
} from '.';

import type { AchievementData } from 'shared-types';

export type AchievementCompletionAddFormValues = AchievementData & {
  addExistingUsers: boolean;
  emails: string[];
  expirationInDays: number[];
  passwords: string[];
  useCommonAttachments: boolean;
  useCommonCompletionDate: boolean;
  useCommonExpirationDate: boolean;
  useCommonStartedAtDate: boolean;
  userExpirationDate?: Date | null;
  usernames: string[];
  userSelector: 'emails' | 'usernames';
};

interface Props {
  alreadyAddedUsersByEmail: { [key: string]: boolean };
  alreadyAddedUsersByUsername: { [key: string]: boolean };
  updateUsers: () => Promise<void>;
  userIds: number[];
  usersWithInProgressAchievementByEmail: { [key: string]: boolean };
  usersWithInProgressAchievementByUsername: { [key: string]: boolean };
}

const AchievementCompletionsAdd = ({
  alreadyAddedUsersByEmail,
  alreadyAddedUsersByUsername,
  updateUsers,
  userIds,
  usersWithInProgressAchievementByEmail,
  usersWithInProgressAchievementByUsername
}: Props) => {
  const { dateNow } = useDateNow();

  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { achievement },
    serverConfig: {
      accountAddForceExpirationDate,
      accountAddForceExpirationDateMaxDays,
      timeZone
    },
    user
  } = useReduxState();

  const commonUploader = useRef<() => void>(undefined);
  const userUploaders = useRef<{ [id: string]: () => void }>({});

  const [newUsers, setNewUsers] = useState<string[]>([]);
  const [state, setState] = useState({ userPasswords: {} });

  const handleHide = () => {
    setNewUsers([]);
    setState({ ...state, userPasswords: {} });
  };

  const setCommonUploader = (fn) => (commonUploader.current = fn);

  const setUserUploader = (id, fn) =>
    (userUploaders.current = { ...userUploaders.current, [id]: fn });

  const submit = async (values: AchievementCompletionAddFormValues) => {
    try {
      const {
        addExistingUsers,
        expirationInDays,
        passwords,
        useCommonAttachments,
        useCommonCompletionDate,
        useCommonExpirationDate,
        useCommonStartedAtDate
      } = values;
      if (!values.userSelector) values.userSelector = 'emails';

      // single attachment for all users?
      const attachments = useCommonAttachments
        ? await commonUploader.current()
        : null;

      const users = values[values.userSelector];

      // ignore users who have in progress achievements
      const usersInProgress =
        values.userSelector === 'emails'
          ? usersWithInProgressAchievementByEmail
          : usersWithInProgressAchievementByUsername;

      // potentially ignore users with a completion
      const usersWithCompletion =
        values.userSelector === 'emails'
          ? alreadyAddedUsersByEmail
          : alreadyAddedUsersByUsername;

      const achievementCompletions = [];
      for (let i = 0; i < users.length; i++) {
        if (usersInProgress[users[i]]) continue;
        if (!addExistingUsers && usersWithCompletion[users[i]]) continue;

        const startedAtDate = useCommonStartedAtDate
          ? values.startedAtDate
          : values[String(i)]?.startedAtDate || null;
        const completionDate = useCommonCompletionDate
          ? values.completionDate
          : values[String(i)]?.completionDate || null;
        const expirationDate = useCommonExpirationDate
          ? values.expirationDate
          : values[String(i)]?.expirationDate || null;
        const comments = values[String(i)]?.comments || null;
        const uploader = useCommonAttachments ? null : userUploaders.current[i];

        const userAttributes = {};

        Object.keys(values[String(i)] || {}).forEach((fieldKey) => {
          if (fieldKey.startsWith('userAttributes-')) {
            const attributeId = fieldKey.split('-')[1];
            userAttributes[attributeId] = values[String(i)][fieldKey];
          }
        });

        const newUser = {
          attachments,
          comments: comments,
          completionDate: completionDate,
          expirationDate: expirationDate,
          startedAtDate: startedAtDate,
          status: ['external', 'milestone'].includes(achievement.type)
            ? 'completed'
            : 'not started',
          ...(values.userSelector === 'emails'
            ? { email: users[i] }
            : { username: users[i] }),
          ...(passwords && {
            password: passwords[i],
            expirationInDays: expirationInDays[i]
          }),
          userAttributes
        };
        if (!useCommonAttachments && uploader) {
          const userAttachments = await uploader();
          newUser.attachments = userAttachments;
        }

        achievementCompletions.push(newUser);
      }

      const response = await api.post(
        `/admin/achievements/${achievement.id}/completions`,
        {
          achievementCompletions,
          userExpirationDate: values.userExpirationDate
            ? toDate(values.userExpirationDate, { timeZone })
            : null
        }
      );

      const successMessage =
        `${response.data.addedUsers} ${messages.settingsAddUsersUsersAdded}, ` +
        `${response.data.newUsers} ${messages.settingsAddUsersAccountsCreated}`;

      await updateUsers();

      userUploaders.current = {};

      dispatch.addNotice({ text: successMessage, type: 'success' });

      if (values.usernames?.length && response.data.newUsers) {
        const userPasswords = {};
        values.passwords.forEach((password, i) => {
          if (password) userPasswords[values.usernames[i]] = password;
        });

        setState({ ...state, userPasswords });
      }
    } catch (err) {
      if (err.message === messages.commonUploadFileFailed) {
        return false; // keep the modal open, user already notified by the uploader
      } else {
        await dispatch.handleErrorDispatch(err);
      }
    }
  };

  const { userPasswords } = state;

  const useAddTranslation = ['conversation', 'tasklist'].includes(
    achievement.type
  );

  const initialValues = {
    addExistingUsers: true,
    completionDate: dateNow,
    emails: [],
    expirationDate: achievement.monthsBeforeExpiration
      ? addMonths(dateNow, achievement.monthsBeforeExpiration)
      : null,
    useCommonAttachments: false,
    useCommonCompletionDate: true,
    useCommonExpirationDate: false,
    useCommonStartedAtDate: false,
    usernames: []
  };

  const jsonImporterEnabled = user?.roles.includes('vuoadmin');

  return (
    <FlexContainer>
      <ModalButtonWithForm
        buttonCategory="success"
        buttonIcon="plus"
        buttonSize="header"
        buttonText={
          useAddTranslation
            ? messages.commonAddUsers
            : messages.commonAddCompletions
        }
        buttonTextHideBreakpoint={breakpoints.medium - 1}
        contentStyle={{ padding: 0 }}
        initialValues={initialValues}
        modalActionText={messages.commonAdd}
        modalTitle={
          useAddTranslation
            ? messages.commonAddUsers
            : messages.commonAddCompletions
        }
        modalWidth="wide"
        onSubmit={submit}
        {...(jsonImporterEnabled
          ? {
              buttonStyle: {
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                marginRight: 1
              }
            }
          : {})}
      >
        {({ values }) => (
          <>
            <div style={{ padding: 15 }}>
              <UserSelector existingUsers={userIds} onNewUsers={setNewUsers} />

              {accountAddForceExpirationDate && newUsers.length > 0 && (
                <FormGroup
                  htmlFor="userExpirationDate"
                  label={messages.commonExpirationDateForNewUserAccounts}
                  style={{ marginTop: 15 }}
                >
                  <Datepicker
                    field="userExpirationDate"
                    id="userExpirationDate"
                    isClearable={false}
                    maxDate={addDays(
                      dateNow,
                      accountAddForceExpirationDateMaxDays
                    )}
                    minDate={dateNow}
                    required={true}
                    validateOnChange={true}
                    validateOnBlur={true}
                    style={{ maxWidth: 200 }}
                    placeholder={messages.commonExpirationDate}
                  />
                </FormGroup>
              )}
            </div>

            <AchievementCompletionsAddInfo
              numberOfUsersInProgress={
                values.emails?.length
                  ? values.emails.filter(
                      (email: string) =>
                        usersWithInProgressAchievementByEmail[email]
                    ).length
                  : values.usernames?.length
                  ? values.usernames.filter(
                      (username: string) =>
                        usersWithInProgressAchievementByUsername[username]
                    ).length
                  : 0
              }
              numberOfUsersWithCompletion={
                values.emails?.length
                  ? values.emails.filter(
                      (email: string) =>
                        alreadyAddedUsersByEmail[email] &&
                        !usersWithInProgressAchievementByEmail[email]
                    ).length
                  : values.usernames?.length
                  ? values.usernames.filter(
                      (username: string) =>
                        alreadyAddedUsersByUsername[username] &&
                        !usersWithInProgressAchievementByUsername[username]
                    ).length
                  : 0
              }
            />

            {values.emails?.length || values.usernames?.length ? (
              <AchievementCompletionsAddHeader
                achievement={achievement}
                setUploader={setCommonUploader}
              />
            ) : null}

            <AchievementCompletionsAddTable
              achievement={achievement}
              alreadyAddedUsersByEmail={alreadyAddedUsersByEmail}
              alreadyAddedUsersByUsername={alreadyAddedUsersByUsername}
              setUploader={setUserUploader}
              usersWithInProgressAchievementByEmail={
                usersWithInProgressAchievementByEmail
              }
              usersWithInProgressAchievementByUsername={
                usersWithInProgressAchievementByUsername
              }
            />
          </>
        )}
      </ModalButtonWithForm>

      {jsonImporterEnabled ? (
        <AchievementImportJson updateData={updateUsers} />
      ) : null}

      <UserPasswordReview
        onModalDismiss={handleHide}
        passwords={userPasswords}
      />
    </FlexContainer>
  );
};

export default AchievementCompletionsAdd;
